/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import {
  Banknote,
  Blocks,
  ShieldCheck,
  FlaskConical,
  PlugZap,
  Lock,
  Languages,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AnimateInView } from '@/components/animate-in-view'

import choose01 from '@/assets/img/choose_01.avif'
import choose02 from '@/assets/img/choose_02.avif'
import choose03 from '@/assets/img/choose_03.avif'
import choose04 from '@/assets/img/choose_041.jpg'
import choose05 from '@/assets/img/choose_05.avif'
import choose06 from '@/assets/img/choose_06.avif'
import choose07 from '@/assets/img/choose_07.avif'

export function WhyKiaApi() {
  const { t } = useTranslation()

  const items = [
    {
      num: '01',
      eyebrow: t('Lower-Cost Access'),
      title: t('Affordable Access, No Hidden Costs'),
      desc: t(
        'Pay by local bank transfer instead of an international card, with transparent per-token pricing shown up front for every model. You only pay for what you actually use.'
      ),
      icon: <Banknote className='size-5' strokeWidth={1.5} />,
      image: choose01,
    },
    {
      num: '02',
      eyebrow: t('Unified API'),
      title: t('One API for Every Model'),
      desc: t(
        'Access Gemini, DeepSeek, and more through a single OpenAI-compatible endpoint. Switch models by changing one parameter, no separate integration for each provider.'
      ),
      icon: <Blocks className='size-5' strokeWidth={1.5} />,
      image: choose02,
    },
    {
      num: '03',
      eyebrow: t('Reliability'),
      title: t('Built on Stable Infrastructure'),
      desc: t(
        'Some models run directly on our own servers, reducing full dependency on any single external provider and keeping response times consistent.'
      ),
      icon: <ShieldCheck className='size-5' strokeWidth={1.5} />,
      image: choose03,
    },
    {
      num: '04',
      eyebrow: t('Playground'),
      title: t('Try It Before You Commit'),
      desc: t(
        'Test any model directly in the Playground, or ask up to 10 questions on the homepage with no account and no signup required.'
      ),
      icon: <FlaskConical className='size-5' strokeWidth={1.5} />,
      image: choose04,
    },
    {
      num: '05',
      eyebrow: t('Drop-in'),
      title: t('Connect in Minutes'),
      desc: t(
        'Point your existing OpenAI-compatible code at our endpoint and change the model name. No backend rewrite, no new SDK to learn.'
      ),
      icon: <PlugZap className='size-5' strokeWidth={1.5} />,
      image: choose05,
    },
    {
      num: '06',
      eyebrow: t('Security'),
      title: t('Your Keys, Protected'),
      desc: t(
        'All traffic is encrypted over HTTPS, and any API key can be revoked instantly the moment it is no longer needed.'
      ),
      icon: <Lock className='size-5' strokeWidth={1.5} />,
      image: choose06,
    },
    {
      num: '07',
      eyebrow: t('Support'),
      title: t('Help in Your Own Language'),
      desc: t(
        'Full Persian documentation, a step-by-step beginner course, and a Telegram bot for direct support — built for Persian-speaking users first.'
      ),
      icon: <Languages className='size-5' strokeWidth={1.5} />,
      image: choose07,
    },
  ]

  return (
    <section className='relative z-10 overflow-hidden px-6 py-24 md:py-32'>
      <div className='mx-auto max-w-6xl'>
        <AnimateInView className='mx-auto mb-16 max-w-lg text-center md:mb-24'>
          <p className='text-muted-foreground mb-3 text-xs font-medium tracking-widest uppercase'>
            {t('Why Kia API')}
          </p>
          <h2 className='text-2xl leading-tight font-bold tracking-tight md:text-3xl'>
            {t('Everything you need,')}
            <br />
            {t('in one place')}
          </h2>
        </AnimateInView>

        <div className='flex flex-col gap-20 md:gap-28'>
          {items.map((item, i) => {
            const imageFirst = i % 2 === 0
            return (
              <div
                key={item.num}
                className='relative'
              >
                <div
                  aria-hidden
                  className='pointer-events-none absolute inset-y-0 left-1/2 -z-10 w-screen -translate-x-1/2 opacity-45 dark:opacity-25'
                  style={{
                    background: [
                      'radial-gradient(ellipse 90% 80% at 20% 30%, oklch(0.72 0.18 250 / 80%) 0%, transparent 75%)',
                      'radial-gradient(ellipse 80% 70% at 80% 20%, oklch(0.65 0.15 200 / 60%) 0%, transparent 75%)',
                      'radial-gradient(ellipse 70% 65% at 40% 80%, oklch(0.70 0.12 280 / 40%) 0%, transparent 75%)',
                    ].join(', '),
                    maskImage:
                      'linear-gradient(to bottom, transparent 0%, black 30%, black 55%, transparent 100%)',
                    WebkitMaskImage:
                      'linear-gradient(to bottom, transparent 0%, black 30%, black 55%, transparent 100%)',
                  }}
                />
                <div className='grid items-center gap-10 p-6 md:grid-cols-2 md:gap-16 md:p-10'>
                <AnimateInView
                  animation={imageFirst ? 'fade-right' : 'fade-left'}
                  className={imageFirst ? 'md:order-1' : 'md:order-2'}
                >
                  <div className='border-border/40 bg-muted/10 group overflow-hidden rounded-2xl border shadow-[0_20px_50px_-25px_rgba(15,23,42,0.25)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_70px_-20px_rgba(15,23,42,0.35)] dark:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.7)] dark:hover:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)]'>
                    <img
                      src={item.image}
                      alt={item.title}
                      className='h-auto w-full transition-transform duration-300 ease-out group-hover:scale-105 group-hover:brightness-105'
                      loading='lazy'
                    />
                  </div>
                </AnimateInView>

                <AnimateInView
                  animation={imageFirst ? 'fade-left' : 'fade-right'}
                  className={imageFirst ? 'md:order-2' : 'md:order-1'}
                >
                  <div className='mb-4 flex items-center gap-3'>
                    <span className='border-brand/30 bg-brand/10 text-brand flex size-9 items-center justify-center rounded-lg border'>
                      {item.icon}
                    </span>
                    <span className='text-brand text-3xl font-bold tabular-nums'>
                      {item.num}
                    </span>
                  </div>
                  <p className='text-brand mb-2 flex items-center gap-2 text-xs font-medium tracking-widest uppercase'>
                    <span className='bg-brand/40 h-px w-6' />
                    {item.eyebrow}
                  </p>
                  <h3 className='mb-3 text-xl leading-snug font-bold tracking-tight md:text-2xl'>
                    {item.title}
                  </h3>
                  <p className='text-muted-foreground text-sm leading-relaxed'>
                    {item.desc}
                  </p>
                </AnimateInView>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
